import React from 'react';
import styled from 'styled-components';

type AvatarColorKey =
  | 'purple'
  | 'blue'
  | 'orange'
  | 'grey'
  | 'green'
  | 'darkgreen'
  | 'pink'
  | 'red';

export interface UserAvatarProps {
  initials: string;
  size: 'small' | 'medium' | 'large' | 'xlarge' | 'topbar';
  colorKey: AvatarColorKey;
  type?: 'normal' | 'clickable';
}

const sizes = {
  small: 24,
  medium: 32,
  large: 40,
  xlarge: 120,
  topbar: 30,
};

const fontSize = {
  small: '12px',
  medium: '14px',
  large: '16px',
  xlarge: '24px',
  topbar: '14px',
};

const AvatarContainer = styled.div<{
  size: 'small' | 'medium' | 'large' | 'xlarge' | 'topbar';
  background: string;
  textColor: string;
  type: 'normal' | 'clickable';
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-family: Inter;
  font-weight: 500;
  line-height: 0px;
  background-color: ${({ background }) => background};
  color: ${({ textColor }) => textColor};
  font-size: ${({ size }) => fontSize[size]};
  width: ${({ size }) => sizes[size]}px;
  height: ${({ size }) => sizes[size]}px;
  cursor: ${({ type }) => (type === 'clickable' ? 'pointer' : undefined)};

  &:hover {
    opacity: ${({ type }) => (type === 'clickable' ? 0.8 : 1)};
    box-shadow: ${({ type }) =>
      type === 'clickable' ? '0 0 0 2px var(--color-border-primary)' : 'none'};
  }

  border: ${({ type }) => (type === 'clickable' ? 'none' : '0')};
`;

const avatarColorMap: Record<AvatarColorKey, { bg: string; text: string }> = {
  purple: {
    bg: 'var(--color-avatar-1-bg)',
    text: 'var(--color-avatar-1-text)',
  },
  blue: { bg: 'var(--color-avatar-2-bg)', text: 'var(--color-avatar-2-text)' },
  orange: {
    bg: 'var(--color-avatar-3-bg)',
    text: 'var(--color-avatar-3-text)',
  },
  grey: { bg: 'var(--color-avatar-4-bg)', text: 'var(--color-avatar-4-text)' },
  green: { bg: 'var(--color-avatar-5-bg)', text: 'var(--color-avatar-5-text)' },
  darkgreen: {
    bg: 'var(--color-avatar-6-bg)',
    text: 'var(--color-avatar-6-text)',
  },
  pink: { bg: 'var(--color-avatar-7-bg)', text: 'var(--color-avatar-7-text)' },
  red: { bg: 'var(--color-avatar-8-bg)', text: 'var(--color-avatar-8-text)' },
};

const UserAvatar: React.FC<UserAvatarProps> = ({
  initials,
  size,
  colorKey,
  type = 'normal',
}) => {
  const { bg, text } = avatarColorMap[colorKey];
  const limitedInitials = initials.slice(0, 3).toUpperCase();

  return (
    <AvatarContainer size={size} background={bg} textColor={text} type={type}>
      {limitedInitials.toUpperCase()}
    </AvatarContainer>
  );
};

export default UserAvatar;
